import type { JSX } from 'react'
import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useMutation } from '@tanstack/react-query'
import type { LoginRequest } from '@/lib/dto'
import { api } from '@/lib/api'
import { ApiError } from '@/lib/api-error'
import { useSessionStore } from '@/stores/session'
import { Button } from '@/ui/primitives/Button'
import { PasswordField, TextField } from '@/ui/primitives/Field'
import { problemText } from '../shared/problem'
import { AuthShell } from './AuthShell'

/**
 * Вход.
 *
 * Правила, из которых собран этот экран:
 *
 *  - access-токен остаётся в памяти SPA (его кладёт `api.auth.login`), refresh
 *    приезжает httpOnly-cookie — фронт её не видит и не хранит;
 *  - при неверных данных сообщение одно и обобщённое: «почта или пароль
 *    неверны». Разделять «нет такого пользователя» и «пароль не тот» — значит
 *    подтверждать существование адреса кому угодно;
 *  - поле кода появляется, только когда сервер ответил `TOTP_REQUIRED`, и сразу
 *    получает фокус: пользователь уже держит телефон в руке;
 *  - поле кода ОДНО на оба случая — шесть цифр из приложения и код
 *    восстановления вида `a3f9k-2mzq7` (docs/06 §2). Заставлять выбирать «а
 *    какой это код» незачем: формы не пересекаются, сервер различит сам, а
 *    человек, потерявший телефон, и так в стрессе;
 *  - валидация на blur, а не на каждый символ: подсказка «некорректный адрес»
 *    посреди набора почты — самая раздражающая мелочь в формах.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Второй фактор: `SecondFactorSchema` сервера — от 6 до 32 символов из
 * латиницы, цифр и дефиса. Ровно это и проверяем: если сузить до шести цифр,
 * код восстановления не пройдёт клиентскую валидацию и до сервера не доедет.
 */
const SECOND_FACTOR_RE = /^[A-Za-z0-9-]{6,32}$/

export function SignInScreen(): JSX.Element {
  const navigate = useNavigate()
  const setSession = useSessionStore(state => state.setSession)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [totp, setTotp] = useState('')
  const [needsTotp, setNeedsTotp] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState<string | null>(null)

  const login = useMutation({
    mutationFn: (dto: LoginRequest) => api.auth.login(dto),
    onSuccess: async () => {
      // `/auth/me` — источник прав и активной организации: без него каркас не
      // знает, что показывать и что запрещать.
      const me = await api.auth.me()
      setSession(me)
      await navigate({ to: '/' })
    },
    onError: error => {
      if (error instanceof ApiError && error.code === 'TOTP_REQUIRED') {
        setNeedsTotp(true)
        setFormError('Нужен код из приложения-аутентификатора')
        return
      }
      if (error instanceof ApiError && error.code === 'UNAUTHORIZED') {
        // Ветвление по тому, был ли уже отправлен код: до второго фактора 401
        // означает «пара почта/пароль не подошла», после — «не сошёлся код».
        // Одно общее сообщение здесь отправляло бы пользователя перепроверять
        // пароль, который на самом деле верен.
        setFormError(
          needsTotp
            ? 'Код не подошёл. Введите свежий код из приложения или код восстановления.'
            : 'Почта или пароль неверны'
        )
        return
      }
      setFormError(problemText(error))
    }
  })

  const validate = (): boolean => {
    const found: Record<string, string> = {}
    if (email.trim() === '') found.email = 'Введите почту'
    else if (!EMAIL_RE.test(email.trim())) found.email = 'Похоже, это не адрес почты'
    if (password === '') found.password = 'Введите пароль'
    if (needsTotp && !SECOND_FACTOR_RE.test(totp.trim())) {
      found.totp = 'Шесть цифр из приложения или код восстановления вида a3f9k-2mzq7'
    }
    setErrors(found)
    return Object.keys(found).length === 0
  }

  return (
    <AuthShell
      title="Вход в панель"
      subtitle="Боты продолжают работать, даже когда панель закрыта."
      footer={
        <>
          Нет аккаунта?{' '}
          <Link to="/register" className="text-honey hover:underline">
            Зарегистрироваться
          </Link>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        noValidate
        onSubmit={event => {
          event.preventDefault()
          setFormError(null)
          if (validate()) {
            login.mutate({
              email: email.trim(),
              password,
              ...(needsTotp ? { totp: totp.trim() } : {})
            })
          }
        }}
      >
        <TextField
          label="Почта"
          type="email"
          autoComplete="username"
          mono
          value={email}
          required
          error={errors.email ?? null}
          onBlur={() => {
            if (email !== '') validate()
          }}
          onChange={event => setEmail(event.target.value)}
        />

        <PasswordField
          label="Пароль"
          autoComplete="current-password"
          value={password}
          required
          error={errors.password ?? null}
          onChange={event => setPassword(event.target.value)}
        />

        {needsTotp ? (
          <TextField
            label="Код подтверждения"
            hint="Шесть цифр из приложения или код восстановления (a3f9k-2mzq7)"
            // Поле монтируется только после ответа TOTP_REQUIRED, поэтому
            // autoFocus здесь срабатывает ровно один раз и в нужный момент.
            autoFocus
            autoComplete="one-time-code"
            maxLength={32}
            mono
            value={totp}
            error={errors.totp ?? null}
            // Чистим только пробелы и запрещённые символы: вырезать всё,
            // кроме цифр, было нельзя — от кода восстановления не осталось бы
            // ничего, кроме цифр, и вход по нему был бы невозможен в принципе.
            onChange={event => setTotp(event.target.value.replace(/[^A-Za-z0-9-]/gu, ''))}
          />
        ) : null}

        {formError === null ? null : (
          <p
            role="alert"
            className="rounded-sm border border-fail/40 bg-fail/12 px-3 py-2 text-base text-fail"
          >
            {formError}
          </p>
        )}

        <Button type="submit" variant="primary" className="mt-1 w-full" loading={login.isPending}>
          Войти
        </Button>
      </form>
    </AuthShell>
  )
}
